import { useTranslation } from '@/i18n'

interface DetailsTabProps {
  docName: string
  documentMetadata?: {
    uploadedBy?: string
    uploadDate?: string
    lastModified?: string
    fileSize?: string
    documentType?: string
  }
}

export function DetailsTab({
  docName,
  documentMetadata
}: DetailsTabProps) {
  const { t } = useTranslation()
  const formatDate = (date: string | undefined) => {
    if (!date) return t('document_viewer:details.notAvailable', { defaultValue: 'N/A' })
    try {
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(',', '')
    } catch {
      return date
    }
  }

  return (
    <div className="h-full overflow-auto bg-white dark:bg-dark-card p-6">
      <div className="space-y-6">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-dark-text mb-6">{t('document_viewer:details.title', { defaultValue: 'Document Details' })}</h2>
        </div>

        {/* min-w-0 on the cells + break-words on the values: grid items
            default to min-width:auto, so a long unbroken filename otherwise
            overflows its column and overlaps the neighboring value. */}
        <div className="grid grid-cols-2 gap-x-12 gap-y-5 text-sm">
          <div className="min-w-0">
            <div className="text-gray-600 dark:text-gray-400 mb-1.5">{t('document_viewer:details.fileName', { defaultValue: 'File Name' })}</div>
            <div className="text-gray-900 dark:text-dark-text break-words">{docName}</div>
          </div>

          <div className="min-w-0">
            <div className="text-gray-600 dark:text-gray-400 mb-1.5">{t('document_viewer:details.fileSize', { defaultValue: 'File Size' })}</div>
            <div className="text-gray-900 dark:text-dark-text break-words">{documentMetadata?.fileSize || t('document_viewer:details.notAvailable', { defaultValue: 'N/A' })}</div>
          </div>

          <div className="min-w-0">
            <div className="text-gray-600 dark:text-gray-400 mb-1.5">{t('document_viewer:details.uploadDate', { defaultValue: 'Upload Date' })}</div>
            <div className="text-gray-900 dark:text-dark-text break-words">{formatDate(documentMetadata?.uploadDate)}</div>
          </div>

          <div className="min-w-0">
            <div className="text-gray-600 dark:text-gray-400 mb-1.5">{t('document_viewer:details.uploadedBy', { defaultValue: 'Uploaded By' })}</div>
            <div className="text-gray-900 dark:text-dark-text break-words">{documentMetadata?.uploadedBy || t('document_viewer:details.unknown', { defaultValue: 'Unknown' })}</div>
          </div>

          <div className="min-w-0">
            <div className="text-gray-600 dark:text-gray-400 mb-1.5">{t('document_viewer:details.lastModified', { defaultValue: 'Last Modified' })}</div>
            <div className="text-gray-900 dark:text-dark-text break-words">{formatDate(documentMetadata?.lastModified)}</div>
          </div>

          <div className="min-w-0">
            <div className="text-gray-600 dark:text-gray-400 mb-1.5">{t('document_viewer:details.documentType', { defaultValue: 'Document Type' })}</div>
            <div className="text-gray-900 dark:text-dark-text break-words">{documentMetadata?.documentType || t('document_viewer:details.documentTypeDefault', { defaultValue: 'Document' })}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
